import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ArrowRight, Info, SearchX } from "lucide-react";
import { catalogueApi, searchApi, type SearchResults } from "../../api/catalogue";
import { ListingResults, type ListingSort } from "../../components/catalogue/ListingResults";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { SearchBox, searchPath } from "../../components/catalogue/SearchBox";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Icon } from "../../components/ui";
import { useListingView } from "../../lib/listingView";
import { recentSearches } from "../../lib/recent";
import { CardAddButton } from "./CardAddButton";
import { usePageTitle } from "./usePageTitle";

/** PRD screen 6: a part number typed any way, or words, with what each result fits. */
export function SearchRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get("q") ?? "").trim();
  // A department chosen on the home page's search bar.
  const typeId = Number(params.get("type")) || undefined;
  usePageTitle(q ? `Search: ${q}` : "Find a part");
  const results = useQuery({ queryKey: ["catalogue", "search", q, typeId], queryFn: () => searchApi.search(q, typeId), enabled: q !== "" });
  const types = useQuery({ queryKey: ["catalogue", "product_types"], queryFn: catalogueApi.productTypes, enabled: typeId !== undefined });
  const department = types.data?.find((type) => type.id === typeId);

  // For the home page's search bar.
  useEffect(() => {
    if (q) recentSearches.add(q);
  }, [q]);

  // A number that names exactly one part goes straight to it (design doc §3). Replacing the
  // history entry keeps Back from landing here and bouncing forward again.
  const single = results.data?.match === "number" && results.data.listings.length === 1 && results.data.replacements.length === 0;
  useEffect(() => {
    if (single) navigate(`/listings/${results.data!.listings[0].id}`, { replace: true });
  }, [single, results.data, navigate]);

  return (
    <PageWidth className="pt-8">
      <h1 className="mb-2 type-h1 [font-size:var(--text-2xl)]">Find a part</h1>
      <p className="mb-5 max-w-[40rem] type-body text-text-muted">
        Type the number on the part, the box or the manufacturer's book, however it's written. Or describe it: "drain plug", "gasket".
      </p>
      <SearchBox key={q} initial={q} size="lg" autoFocus={!q} onSearch={(query) => navigate(searchPath(query, typeId))} className="max-w-[640px]" />
      {typeId !== undefined && (
        <p className="mt-3 mb-0 type-small text-text-muted">
          Only in {department ? <span className="type-body-strong text-text-strong">{department.name}</span> : "one department"}.{" "}
          <Link to={searchPath(q)}>Search everything</Link>
        </p>
      )}

      <div className="mt-8">
        {!q ? null : results.isPending || single ? (
          <p className="type-body text-text-muted">Searching…</p>
        ) : results.isError ? (
          <LoadError onRetry={() => results.refetch()} />
        ) : (
          <Results q={q} results={results.data} />
        )}
      </div>
    </PageWidth>
  );
}

function Results({ q, results }: { q: string; results: SearchResults }) {
  const [view, setView] = useListingView();
  // Parts made to one standard differ by size, so they open sorted by it.
  const [sort, setSort] = useState<ListingSort>(results.match === "standard" ? "size" : "default");
  const listings = results.listings;

  if (results.match === "none" && results.machines.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={`Nothing matches "${q}"`}
        action={
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/equipment-categories">Browse machines</Link>
            <Link to="/ask">Ask us about it</Link>
          </div>
        }
      >
        Check the number stamped on the part, or find it on your machine's diagram instead. Still can't find it? Ask us and we'll help.
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {results.replacements.length > 0 && (
        <div className="flex items-start gap-3 rounded-md border border-gold-300 bg-surface-tint-gold p-4">
          <Icon icon={Info} size={20} className="mt-0.5 text-gold-700" />
          <p className="mb-0 type-body text-ink-700">
            <span className="type-sku">{q}</span> is a part we no longer sell. It's been replaced by{" "}
            {results.replacements.map((part, index) => (
              <span key={part.id}>
                {index > 0 && ", "}
                <Link to={`/listings/${part.id}`}>{part.name}</Link>
              </span>
            ))}
            .
          </p>
        </div>
      )}

      {results.match === "standard" && (
        <div className="flex items-start gap-3 rounded-md border border-border-subtle bg-surface-sunken p-4">
          <Icon icon={Info} size={20} className="mt-0.5 text-gold-700" />
          <div>
            <p className="mb-1 type-body-strong text-text-strong">
              <span className="type-sku">{q}</span> is a standard, not one part
            </p>
            <p className="mb-0 type-small text-text-body">
              {results.listings.length} parts are made to it. They differ by size: match the one you measured.
            </p>
          </div>
        </div>
      )}

      {results.machines.length > 0 && (
        <section>
          <h2 className="mb-2 type-h4 [font-size:var(--text-sm)]">Machines</h2>
          <div className="flex flex-wrap gap-2">
            {results.machines.map((machine) => (
              <Link
                key={machine.id}
                to={`/equipment-models/${machine.id}`}
                className="inline-flex min-h-11 items-center gap-2 rounded-md border border-border-default bg-surface-card px-4 type-body text-text-strong hover:border-green-700"
              >
                {machine.name} <span className="type-small text-text-muted">{machine.equipment_category_name}</span>
                <Icon icon={ArrowRight} size={16} className="text-ink-400" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {listings.length > 0 && (
        <section>
          <ListingResults
            listings={listings}
            defaultSortLabel={results.match === "text" ? "Best match" : "Part number"}
            sort={sort}
            onSort={setSort}
            view={view}
            onView={setView}
            summary={
              <h2 className="m-0 type-h4 [font-size:var(--text-sm)]">
                {listings.length} {listings.length === 1 ? "part" : "parts"}
                {results.truncated && ", the best matches"}
              </h2>
            }
            add={(listing, compact) => <CardAddButton listing={listing} compact={compact} />}
          />
          {results.truncated && <p className="mt-3 mb-0 type-small text-text-muted">There are more. Add a word or part of a number to narrow it down.</p>}
        </section>
      )}
    </div>
  );
}

